import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../AuthContext';
import type { completionFunctionalToolsCallsLogType, SessionContextType, SessionSummary, SessionRenameOptions, SessionUsageInfo } from '../api.types';

import ChatInterface from '../components/ChatInterface';
import BriefSidebar from '../components/BriefSidebar';
import { useSession } from '../components/SessionDropdown';

import useMediaQuery from '../components/hooks/useMediaQuery';
import { parseAgentChatDeepLink } from '../utils/agentChatDeepLink';

import BlockMenu from "../imports/BlockMenu";
import '../styles/agent-page.css';

const briefDataInit = {
  businessType: '',
  product: '',
  businessName: '',
  targetAudience: '',
  serviceOffering: '',
  websiteGoal: ''
};

type BriefField = keyof typeof briefDataInit;

type BriefData = Record<BriefField, string>;
type ActiveFields = Record<BriefField, boolean>;
type FieldWeights = Record<BriefField, number>;

const MainPage: React.FC = () => {
  const [briefData, setBriefData] = useState<BriefData>(briefDataInit);
  const fieldWeights: FieldWeights = {
    businessName: 15,
    businessType: 15,
    product: 15,
    targetAudience: 20,
    serviceOffering: 15,
    websiteGoal: 20
  };
  const [currentStep, setCurrentStep] = useState(0);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [briefStatus, setBriefStatus] = useState('Empty brief');
  const [briefColor, setBriefColor] = useState('#e82729');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingField, setEditingField] = useState<{ name: BriefField; value: string }>({ name: 'businessType', value: '' });
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [lastToolsCalls, setLastToolsCalls] = useState<completionFunctionalToolsCallsLogType[]>([]);
  const [totalTokens, setTotalTokens] = useState<number>(0);
  const [allowedTools, setAllowedTools] = useState<string[]>([]);
  const [sessionCurrentContext, setSessionCurrentContext] = useState<SessionContextType | null>(null);
  const [sessionContextHistory, setSessionContextHistory] = useState<SessionContextType[]>([]);
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);
  const [sessionRenameOptions, setSessionRenameOptions] = useState<SessionRenameOptions | null>(null);
  const [sessionUsage, setSessionUsage] = useState<SessionUsageInfo | null>(null);
  const [activeFields, setActiveFields] = useState<ActiveFields>({
    businessType: false,
    product: false,
    businessName: false,
    targetAudience: false,
    serviceOffering: false,
    websiteGoal: false
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { selectedSession, hydrateSelectedSession } = useSession();
  const agentChatDeepLink = useMemo(() => parseAgentChatDeepLink(location.hash), [location.hash]);

  useEffect(() => {
    if (
      !agentChatDeepLink
      || selectedSession?.id === agentChatDeepLink.sessionId
      || !hydrateSelectedSession
    ) {
      return;
    }

    hydrateSelectedSession(agentChatDeepLink.sessionId).catch(() => undefined);
  }, [agentChatDeepLink, hydrateSelectedSession, selectedSession?.id]);

  useEffect(() => {
    let totalProgress = 0;
    (Object.entries(briefData) as [BriefField, string][]).forEach(([field, value]) => {
      if (value.trim() !== '' && activeFields[field]) {
        totalProgress += fieldWeights[field];
      }
    });
    const newPercentage = Math.round(totalProgress);
    setCompletionPercentage(newPercentage);
    if (newPercentage === 0) {
      setBriefStatus('Empty brief');
      setBriefColor('#e82729');
    } else if (newPercentage < 30) {
      setBriefStatus('Weak brief');
      setBriefColor('#e82729');
    } else if (newPercentage < 60) {
      setBriefStatus('Basic brief');
      setBriefColor('#FFA500');
    } else if (newPercentage < 90) {
      setBriefStatus('Good brief');
      setBriefColor('#2ecc71');
    } else {
      setBriefStatus('Complete brief');
      setBriefColor('#165BFB');
    }
  }, [briefData, activeFields]);

  useEffect(() => {
    if (!isMobile) {
      setShowMobileSidebar(false);
    }
  }, [isMobile]);

  const updateBrief = (field: string, value: string) => {
    if (value.trim()) {
      setBriefData(prev => ({
        ...prev,
        [field]: value
      }));
      if (!activeFields[field as BriefField]) {
        setActiveFields(prev => ({
          ...prev,
          [field]: true
        }));
      }
    }
  };

  const deleteBrief = (field: string) => {
    setBriefData(prev => ({
      ...prev,
      [field]: ''
    }));
    setActiveFields(prev => ({
      ...prev,
      [field]: false
    }));
  };

  const startEditingField = (field: BriefField, value: string) => {
    setEditingField({ name: field, value });
    setShowEditModal(true);
  };

  const saveEditedField = (value: string) => {
    if (value.trim()) {
      updateBrief(editingField.name, value);
    } else {
      deleteBrief(editingField.name);
    }
    setShowEditModal(false);
  };

  const cancelEditing = () => {
    setShowEditModal(false);
  };

  const toggleMobileSidebar = () => {
    setShowMobileSidebar(prev => !prev);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <motion.div
      className="agent-page relative w-full h-screen overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Mobile Brief Toggle Button - only visible on mobile */}
      {isMobile && (
        <motion.button
          className="fixed bottom-4 right-4 z-50 bg-[#165BFB] text-white rounded-full size-14 flex items-center justify-center shadow-lg mobile-brief-toggle"
          onClick={toggleMobileSidebar}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 20 20.1 20 19V5C20 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" fill="white"/>
            <path d="M18 9H6V7H18V9ZM18 13H6V11H18V13ZM18 17H6V15H18V17Z" fill="white"/>
          </svg>
          <span className="ml-1 sr-only">Toggle Brief</span>
        </motion.button>
      )}

      {/* Main layout */}
      <div className="agent-layout flex flex-col h-screen relative z-10">
        {/* Header */}
        <motion.div
          className="agent-header w-full bg-white"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <BlockMenu onLogout={handleLogout} appearance="agent" />
        </motion.div>

        {/* Content area */}
        <div className={`agent-content flex flex-1 overflow-hidden ${isMobile ? 'flex-col' : ''}`}>
          <motion.div
            className={`agent-conversation relative ${isMobile ? 'flex-1 w-full' : 'flex-1 w-[1078px]'}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <ChatInterface
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              updateBrief={updateBrief}
              isMobile={isMobile}
              allowedTools={allowedTools}
              targetSessionId={agentChatDeepLink?.sessionId}
              targetMessageId={agentChatDeepLink?.messageId}
              setLastToolsCalls={setLastToolsCalls}
              setTotalTokens={setTotalTokens}
              onSessionContextChange={setSessionCurrentContext}
              onSessionContextHistoryChange={setSessionContextHistory}
              onSessionSummaryChange={setSessionSummary}
              onSessionRenameOptionsChange={setSessionRenameOptions}
              onSessionUsageChange={setSessionUsage}
            />
          </motion.div>

          {/* Desktop sidebar */}
          {!isMobile && (
            <motion.div
              className="agent-sidebar-column w-[380px] h-full"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <BriefSidebar
                isMobile={false}
                onClose={() => setShowMobileSidebar(false)}
                lastToolsCalls={lastToolsCalls}
                totalTokens={totalTokens}
                allowedTools={allowedTools}
                onAllowedToolsChange={setAllowedTools}
                sessionCurrentContext={sessionCurrentContext}
                sessionContextHistory={sessionContextHistory}
                sessionSummary={sessionSummary}
                onSummaryUpdate={setSessionSummary}
                sessionRenameOptions={sessionRenameOptions}
                onRenameOptionsUpdate={setSessionRenameOptions}
                sessionUsage={sessionUsage}
              />
            </motion.div>
          )}

          {/* Mobile sidebar - slide in from bottom */}
          {isMobile && (
            <AnimatePresence>
              {showMobileSidebar && (
                <>
                  <motion.div
                    className="agent-overlay fixed inset-0 bg-black/40 z-40"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowMobileSidebar(false)}
                  />
                  <motion.div
                    className="agent-mobile-drawer fixed bottom-0 left-0 right-0 z-50 max-h-[80vh] overflow-hidden"
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 25 }}
                  >
                    <div className="flex justify-center w-full py-2 bg-white rounded-t-xl">
                      <div className="w-12 h-1 bg-gray-300 rounded-full" />
                    </div>
                    <BriefSidebar
                      isMobile={true}
                      onClose={() => setShowMobileSidebar(false)}
                      lastToolsCalls={lastToolsCalls}
                      totalTokens={totalTokens}
                      allowedTools={allowedTools}
                      onAllowedToolsChange={setAllowedTools}
                      sessionCurrentContext={sessionCurrentContext}
                      sessionContextHistory={sessionContextHistory}
                      sessionSummary={sessionSummary}
                      onSummaryUpdate={setSessionSummary}
                      sessionRenameOptions={sessionRenameOptions}
                      onRenameOptionsUpdate={setSessionRenameOptions}
                      sessionUsage={sessionUsage}
                    />
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Edit Field Modal */}
      {showEditModal && (
        <div className="agent-overlay fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            className={`agent-dialog bg-white rounded-xl p-6 shadow-lg ${isMobile ? 'w-[90%] max-w-[400px]' : 'w-[400px]'}`}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 25 }}
          >
            <h3 className="font-bold text-[20px] mb-4">
              Edit {editingField.name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            </h3>
            <div className="mb-4">
              <input
                type="text"
                value={editingField.value}
                onChange={(e) => setEditingField(prev => ({ ...prev, value: e.target.value }))}
                className="w-full border border-[#EDF0F5] h-12 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelEditing}
                className="px-5 py-2.5 rounded-lg border border-[#EDF0F5] text-[#464C5C] transition-all hover:bg-[#f6f8ff]"
              >
                Cancel
              </button>
              <button
                onClick={() => saveEditedField(editingField.value)}
                className="px-5 py-2.5 bg-[#165BFB] text-white rounded-lg transition-all hover:bg-[#2044cc] active:bg-[#1c3aad]"
              >
                Save
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default MainPage;
